import { IconButton } from '@mui/material'
import { ButtonV2, TextInputV4 } from '@labrav/react-components'
import { useStyles } from './OfferRecipientV4.styles'
import React, { useCallback, useMemo } from 'react'
import { FormikProps, FieldArray } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { get } from 'lodash'
import { OfferRecipients } from '../../../types/flyout'
import clsx from 'clsx'

const EMPTY_RECIPIENT = {
  email: '',
  firstName: '',
  lastName: '',
  title: '',
} as const

const RECIPIENT_FIELDS = [
  {
    key: 'firstName' as const,
    title: 'First name',
    useHandleChange: false,
  },
  {
    key: 'lastName' as const,
    title: 'Last name',
    useHandleChange: false,
  },
  {
    key: 'title' as const,
    title: 'Title (Optional)',
    useHandleChange: false,
  },
  {
    key: 'email' as const,
    title: 'Email',
    useHandleChange: true,
  },
] as const

const getFieldErrorState = (
  touched: FormikProps<Record<string, unknown>>['touched'],
  errors: FormikProps<Record<string, unknown>>['errors'],
  path: string
) => {
  const isTouched = !!get(touched, path, false)
  const fieldError = get(errors, path, false)
  const isError = !!(isTouched && fieldError)
  const errorText = (isTouched && get(errors, path, ' ')) || ' '
  return { isError, errorText }
}

export const OfferRecipientV4 = React.memo(
  ({
    formik,
    keyName = 'offerRecipients',
    existingRecipientsEmails = [],
    showRemoveIconForAll = true,
    customClassName = '',
  }: {
    formik: FormikProps<Record<string, unknown>>
    keyName?: string
    existingRecipientsEmails?: string[]
    showRemoveIconForAll?: boolean
    customClassName?: string
  }) => {
    const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
      formik
    const { classes } = useStyles()

    const offerRecipients = useMemo(
      () =>
        ((values[keyName] || []) as OfferRecipients[]).filter(
          recipient => !existingRecipientsEmails.includes(recipient.email)
        ),
      [values, keyName, existingRecipientsEmails]
    )

    const formChangeHandler = useCallback(
      (fieldPath: string) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setFieldValue(fieldPath, event.target.value)
        },
      [setFieldValue]
    )

    return (
      <div
        className={classes.recipientsRowsContainer}
        data-testid="update-recipients"
      >
        <FieldArray name={keyName}>
          {({ push, remove }) => (
            <>
              {offerRecipients.map((recipient, index) => (
                <div
                  key={`${keyName}-${index}-${offerRecipients.length}`}
                  className={clsx(classes.updateRow, customClassName)}
                  data-testid="recipient-row"
                >
                  <div className={classes.recipientFieldsGrid}>
                    {RECIPIENT_FIELDS.map(
                      ({ key: fieldKey, title, useHandleChange }) => {
                        const path = `${keyName}.${index}.${fieldKey}`
                        const { isError, errorText } = getFieldErrorState(
                          touched,
                          errors,
                          path
                        )
                        return (
                          <div key={path} className={classes.fieldCell}>
                            <TextInputV4
                              isReadOnly={false}
                              placeHolder=""
                              isRequired={false}
                              keyName={path}
                              onChange={
                                useHandleChange
                                  ? handleChange
                                  : formChangeHandler(path)
                              }
                              type="string"
                              title={title}
                              value={recipient[fieldKey] || ''}
                              isError={isError}
                              errorText={errorText}
                              onBlur={handleBlur}
                            />
                          </div>
                        )
                      }
                    )}
                  </div>
                  <div className={classes.deleteWrapper}>
                    <RemoveRecipient
                      index={index}
                      recipientCount={offerRecipients.length}
                      remove={remove}
                      showRemoveIconForAll={showRemoveIconForAll}
                    />
                  </div>
                </div>
              ))}

              <ButtonV2
                className={classes.addRecipientBtn}
                styleType="link"
                smallVariant
                onClick={() => {
                  push({ ...EMPTY_RECIPIENT })
                }}
                data-testid="add-a-recipient"
                icon={
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{ width: '14px', height: '16px' }}
                  />
                }
              >
                Add recipients
              </ButtonV2>
            </>
          )}
        </FieldArray>
      </div>
    )
  }
)

export const RemoveRecipient = React.memo(
  ({
    recipientCount,
    index,
    remove,
    showRemoveIconForAll = true,
  }: {
    recipientCount: number
    index: number
    remove: <X = unknown>(index: number) => X | undefined
    showRemoveIconForAll?: boolean
  }) => {
    const { classes } = useStyles()
    if (recipientCount <= 1 && !showRemoveIconForAll) {
      return null
    }
    return (
      <IconButton
        className={classes.removeIconPadding}
        onClick={() => remove(index)}
        data-testid="cancel-button"
        size="large"
      >
        <FontAwesomeIcon icon={faTrash} width={'14px'} height={'20px'} />
      </IconButton>
    )
  }
)
